import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

type QrCameraViewProps = {
  onScan: (decodedText: string) => void;
  onPermissionDenied: () => void;
};

export function QrCameraView({ onScan, onPermissionDenied }: QrCameraViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<any>(null);
  const hasScannedRef = useRef(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function startScanner() {
      const { Html5Qrcode } = await import("html5-qrcode");

      if (!mounted || !containerRef.current) return;

      const scannerId = "qr-scanner-" + Date.now();
      containerRef.current.id = scannerId;

      const scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1,
          },
          (decodedText: string, _result: unknown) => {
            if (hasScannedRef.current) return;
            hasScannedRef.current = true;
            onScan(decodedText);
          },
          (_errorMessage: string, _error: unknown) => {
            // ignore decode failures (normal while scanning)
          }
        );
        if (mounted) setLoading(false);
      } catch {
        if (mounted) onPermissionDenied();
      }
    }

    startScanner();

    return () => {
      mounted = false;
      const scanner = scannerRef.current;
      if (scanner) {
        scanner.stop().then(() => scanner.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [onScan, onPermissionDenied]);

  return (
    <div className="relative flex flex-col items-center justify-center h-full">
      {loading && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
          <p className="mt-3 text-sm text-white/60">Starting camera...</p>
        </div>
      )}

      {/* Camera container */}
      <div ref={containerRef} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />

      {/* Corner bracket overlay */}
      {!loading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="relative h-[250px] w-[250px]">
            {/* Top-left */}
            <span className="absolute top-0 left-0 h-8 w-8 border-t-[3px] border-l-[3px] border-[#2D5A4A] rounded-tl-lg" />
            {/* Top-right */}
            <span className="absolute top-0 right-0 h-8 w-8 border-t-[3px] border-r-[3px] border-[#2D5A4A] rounded-tr-lg" />
            {/* Bottom-left */}
            <span className="absolute bottom-0 left-0 h-8 w-8 border-b-[3px] border-l-[3px] border-[#2D5A4A] rounded-bl-lg" />
            {/* Bottom-right */}
            <span className="absolute bottom-0 right-0 h-8 w-8 border-b-[3px] border-r-[3px] border-[#2D5A4A] rounded-br-lg" />

            {/* Scan line animation */}
            <div className="absolute left-2 right-2 h-0.5 bg-[#2D5A4A]/60 animate-[scan_2s_ease-in-out_infinite]" />
          </div>
        </div>
      )}

      {/* Instruction text */}
      {!loading && (
        <div className="absolute bottom-12 left-0 right-0 text-center">
          <p className="text-sm text-white/80 bg-black/40 inline-block px-4 py-2 rounded-full">
            Point at the kiosk QR code
          </p>
        </div>
      )}

      <style>{`
        @keyframes scan {
          0%, 100% { top: 8px; }
          50% { top: calc(100% - 10px); }
        }
      `}</style>
    </div>
  );
}
